// ==================================
// Pagina Dashboard
// ==================================

import { useQuery } from '@tanstack/react-query';
import { Header } from '@/componentes/layout';
import { Card, Spinner } from '@/componentes/ui';
import { dashboardServicio } from '@/servicios';

function StatCard({
  titulo,
  valor,
  subtitulo,
  icono,
  color,
}: {
  titulo: string;
  valor: string | number;
  subtitulo?: string;
  icono: React.ReactNode;
  color: string;
}) {
  return (
    <Card>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-stone-500">{titulo}</p>
          <p className="text-2xl font-bold text-obsidian-900 mt-1">{valor}</p>
          {subtitulo && (
            <p className="text-sm text-stone-500 mt-1">{subtitulo}</p>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>{icono}</div>
      </div>
    </Card>
  );
}

export function Dashboard() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: dashboardServicio.obtenerEstadisticas,
  });

  const formatMoney = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(amount);
  };

  return (
    <div>
      <Header titulo="Dashboard" />

      <div className="p-6">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : stats ? (
          <div className="space-y-6">
            {/* Veterinarias Stats */}
            <div>
              <h2 className="text-lg font-semibold text-obsidian-900 mb-4">
                Veterinarias
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                  titulo="Total Veterinarias"
                  valor={stats.veterinarias.total}
                  icono={
                    <svg
                      className="w-6 h-6 text-primary-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  }
                  color="bg-primary-100"
                />
                <StatCard
                  titulo="Activas"
                  valor={stats.veterinarias.activas}
                  icono={
                    <svg
                      className="w-6 h-6 text-emerald-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  }
                  color="bg-emerald-100"
                />
                <StatCard
                  titulo="Inactivas"
                  valor={stats.veterinarias.inactivas}
                  icono={
                    <svg
                      className="w-6 h-6 text-red-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  }
                  color="bg-red-100"
                />
                <StatCard
                  titulo="Nuevas este mes"
                  valor={stats.veterinarias.nuevasEsteMes}
                  icono={
                    <svg
                      className="w-6 h-6 text-gold-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                  }
                  color="bg-gold-100"
                />
              </div>
            </div>

            {/* Ingresos Stats */}
            <div>
              <h2 className="text-lg font-semibold text-obsidian-900 mb-4">
                Ingresos
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard
                  titulo="Ingresos del Mes"
                  valor={formatMoney(stats.ingresos.totalMes)}
                  icono={
                    <svg
                      className="w-6 h-6 text-emerald-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  }
                  color="bg-emerald-100"
                />
                <StatCard
                  titulo="MRR"
                  valor={formatMoney(stats.ingresos.mrr)}
                  subtitulo="Ingreso mensual recurrente"
                  icono={
                    <svg
                      className="w-6 h-6 text-primary-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                      />
                    </svg>
                  }
                  color="bg-primary-100"
                />
                <StatCard
                  titulo="ARR"
                  valor={formatMoney(stats.ingresos.arr)}
                  subtitulo="Ingreso anual recurrente"
                  icono={
                    <svg
                      className="w-6 h-6 text-gold-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                  }
                  color="bg-gold-100"
                />
              </div>
            </div>

            {/* Licencias Stats */}
            <div>
              <h2 className="text-lg font-semibold text-obsidian-900 mb-4">
                Estado de Licencias
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                  titulo="Activas"
                  valor={stats.licencias.activas}
                  icono={
                    <svg
                      className="w-6 h-6 text-emerald-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    </svg>
                  }
                  color="bg-emerald-100"
                />
                <StatCard
                  titulo="En Periodo de Gracia"
                  valor={stats.licencias.enGracia}
                  icono={
                    <svg
                      className="w-6 h-6 text-gold-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  }
                  color="bg-gold-100"
                />
                <StatCard
                  titulo="Expiradas"
                  valor={stats.licencias.expiradas}
                  icono={
                    <svg
                      className="w-6 h-6 text-red-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                      />
                    </svg>
                  }
                  color="bg-red-100"
                />
                <StatCard
                  titulo="En Prueba"
                  valor={stats.licencias.prueba}
                  icono={
                    <svg
                      className="w-6 h-6 text-primary-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
                      />
                    </svg>
                  }
                  color="bg-primary-100"
                />
              </div>
            </div>
          </div>
        ) : (
          <Card className="text-center py-12">
            <p className="text-stone-500">No se pudieron cargar las estadísticas</p>
          </Card>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
